import { useState } from "react";
import { toast } from "sonner";
import { api } from "./api";

const BADGE = { needs_review: "Needs Alias review", unsupported: "Not supported in V1" };
const field = "rounded-xl bg-slate-950 ring-1 ring-slate-700 p-2.5 text-sm text-slate-100 focus:outline-none focus:ring-amber-500";

function Item({ u, i, options, value, onChange }) {
  const d = u.decision;
  return (
    <li className="rounded-xl bg-slate-950/60 ring-1 ring-amber-500/30 p-3 space-y-2" data-testid={`unresolved-item-${i}`}>
      <p className="text-xs"><span className="rounded px-1.5 py-0.5 text-[10px] bg-amber-500/15 text-amber-300" data-testid={`unresolved-outcome-${i}`}>{BADGE[u.outcome]} — not completed</span></p>
      <p className="text-sm text-slate-100 whitespace-pre-wrap break-words" data-testid={`unresolved-text-${i}`}>{u.request}</p>
      <p className="text-xs text-slate-400">{u.explanation}</p>
      {d && (
        <p className="text-xs text-emerald-300" data-testid={`unresolved-decision-${i}`}>
          Decision: {options[d.decision]}{d.note ? ` — "${d.note}"` : ""} ({d.actor}, {new Date(d.at).toLocaleString()})
        </p>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <select value={value.decision} onChange={(e) => onChange({ ...value, decision: e.target.value })}
          data-testid={`unresolved-select-${i}`} className={`${field} sm:w-64`}>
          <option value="">{d ? "Change decision…" : "Choose what to do…"}</option>
          {Object.entries(options).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={value.note} maxLength={500} onChange={(e) => onChange({ ...value, note: e.target.value })}
          placeholder={value.decision === "done_another_way" ? "Required: how it was done (shown to the client)" : "Message for the client (optional)"}
          data-testid={`unresolved-note-${i}`} className={`${field} flex-1`} />
      </div>
    </li>
  );
}

// TEST-050: Alias must decide on every client request that was not completed before the site goes back to the client.
export function UnresolvedRequests({ jobId, items, options, onDone }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  if (!items || items.length === 0) return null;
  const undecided = items.filter((u) => !u.decision).length;
  const keyOf = (u) => `${u.rid}#${u.n}`;
  const get = (u) => vals[keyOf(u)] || { decision: "", note: "" };
  const chosen = items.filter((u) => get(u).decision).map((u) => ({ rid: u.rid, n: u.n, ...get(u) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.requestDecisions(jobId, chosen);
      toast.success("Decisions recorded");
      setVals({}); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="unresolved-requests">
      <p className="text-sm font-semibold text-amber-200" data-testid="unresolved-summary">
        {undecided > 0
          ? `${undecided} client request${undecided > 1 ? "s were" : " was"} not completed — choose what to do before sending the website back to the client.`
          : "All client requests that were not completed have a recorded decision. The client will see these listed as not changed."}
      </p>
      <ul className="space-y-2">
        {items.map((u, i) => <Item key={keyOf(u)} u={u} i={i} options={options} value={get(u)}
          onChange={(v) => setVals({ ...vals, [keyOf(u)]: v })} />)}
      </ul>
      <button onClick={save} disabled={busy || !chosen.length} data-testid="unresolved-save"
        className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50 transition-colors">
        {busy ? "Saving…" : "Save decisions"}
      </button>
    </div>
  );
}
